import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { BookingForm } from "./booking/BookingForm";
import { DateTimeSelector } from "./booking/DateTimeSelector";
import { ProviderSelector } from "./booking/ProviderSelector";
import { ProgressSteps } from "./booking/ProgressSteps";
import { NavigationButtons } from "./booking/NavigationButtons";
import { DraftIndicator } from "./booking/DraftIndicator";
import { useBookingState } from "@/hooks/useBookingState";
import { validateBookingStep1, validateBookingStep2 } from "@/utils/validationUtils";
import { useToast } from "@/hooks/use-toast";
import { useProviders } from "@/hooks/useProvidersQuery";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export function PatientBooking() {
  const [bookingStep, setBookingStep] = useState(1);
  const { profile } = useAuth();
  const { 
    bookingState, 
    isLoading: bookingStateLoading, 
    updateProvider, 
    updateDateTime, 
    updatePatientInfo, 
    clearBooking, 
    hasDraft 
  } = useBookingState();
  const { data: providers, isLoading: providersLoading } = useProviders(profile?.clinic_id || '');
  const { toast } = useToast();

  // Add debugging logs
  useEffect(() => {
    console.log('Profile:', profile);
    console.log('Clinic ID:', profile?.clinic_id);
    console.log('Providers:', providers);
    console.log('Providers Loading:', providersLoading);
  }, [profile, providers, providersLoading]);

  const selectedProviderData = providers?.find(p => p.id === bookingState.selectedProvider);

  const handleCompleteBooking = () => {
    clearBooking();
    setBookingStep(1);
    toast({
      title: "Booking Complete! 🎉",
      description: "Your appointment has been successfully scheduled.",
    });
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (bookingStep === 1) {
      const errors = validateBookingStep1(bookingState.selectedProvider);
      if (errors.length > 0) {
        toast({
          title: "Validation Error",
          description: errors[0].message,
          variant: "destructive",
        });
        return;
      }
    } else if (bookingStep === 2) {
      const errors = validateBookingStep2(bookingState.selectedDate, bookingState.selectedTime);
      if (errors.length > 0) {
        toast({
          title: "Validation Error", 
          description: errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }
    
    setBookingStep(bookingStep + 1);
  };

  const handlePrevious = () => {
    setBookingStep(Math.max(1, bookingStep - 1));
  };

  const handleDateTimeChange = (date: Date | undefined, time: string) => {
    updateDateTime(date, time);
  };

  const canProceedToStep2 = Boolean(bookingState.selectedProvider);
  const canProceedToStep3 = Boolean(bookingState.selectedDate && bookingState.selectedTime);

  const isLoading = bookingStateLoading || providersLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Loading providers...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile?.clinic_id) {
    return (
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Clinic Associated</h2>
            <p className="text-gray-600">Please contact your clinic administrator to get access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!providers || providers.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Providers Available</h2>
            <p className="text-gray-600">There are currently no healthcare providers available for booking. Please check back later or contact the clinic.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patient Booking</h1>
          <p className="text-gray-600">Easy online appointment scheduling for patients.</p>
        </div>
        <div className="flex items-center gap-3">
          {hasDraft() && <DraftIndicator />}
          <Badge variant="outline" className="px-3 py-1">
            Step {bookingStep} of 3
          </Badge>
        </div>
      </div>

      <ProgressSteps currentStep={bookingStep} />

      {/* Step 1: Select Provider */}
      {bookingStep === 1 && (
        <ProviderSelector
          providers={providers}
          selectedProvider={bookingState.selectedProvider}
          onProviderSelect={updateProvider}
        />
      )}

      {/* Step 2: Select Date & Time */}
      {bookingStep === 2 && (
        <DateTimeSelector
          selectedDate={bookingState.selectedDate}
          selectedTime={bookingState.selectedTime}
          onDateSelect={(date) => handleDateTimeChange(date, bookingState.selectedTime)}
          onTimeSelect={(time) => handleDateTimeChange(bookingState.selectedDate, time)}
        />
      )}

      {/* Step 3: Patient Information */}
      {bookingStep === 3 && selectedProviderData && bookingState.selectedDate && (
        <BookingForm
          provider={selectedProviderData}
          selectedDate={format(bookingState.selectedDate, "EEEE, MMMM d, yyyy")}
          selectedTime={bookingState.selectedTime}
          patientInfo={bookingState.patientInfo}
          onPatientInfoChange={updatePatientInfo}
          onComplete={handleCompleteBooking}
        />
      )}

      <NavigationButtons
        currentStep={bookingStep}
        canProceedToStep2={canProceedToStep2}
        canProceedToStep3={canProceedToStep3}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </div>
  );
}
